import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { useAuth } from '../context/AuthContext';
import { API } from '../lib/api';
import axios from 'axios';
import { Clapperboard, UploadCloud, CheckCircle2, Clock, XCircle, Film, ArrowLeft, Link2, ShieldCheck, Scale, Award } from 'lucide-react';
import { toast } from 'sonner';

const THEMES = ['Code de la route', 'Circulation', 'Sécurité routière', 'Moto', 'Giratoire', 'Stationnement', 'Autre'];
const PLATFORMS_HINT = 'YouTube (recommandé), Vimeo, Dailymotion, TikTok, Instagram — HTTPS uniquement';
const CHUNK = 2 * 1024 * 1024;

// crypto.randomUUID n'existe pas sur iOS < 15.4 → fallback sûr
const makeUploadId = () => (window.crypto?.randomUUID
  ? window.crypto.randomUUID()
  : `up-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

const STATUS_UI = {
  pending: { label: 'En attente de validation', icon: Clock, cl: 'bg-amber-950/30 border-amber-600/40 text-amber-300' },
  approved: { label: 'Publié dans les Shorts', icon: CheckCircle2, cl: 'bg-emerald-950/30 border-emerald-600/40 text-emerald-300' },
  rejected: { label: 'Refusé', icon: XCircle, cl: 'bg-red-950/30 border-red-600/40 text-red-300' },
};

export default function ProposerShortPage() {
  const { user, loading, openLogin, openRegister } = useAuth();
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('link');
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [theme, setTheme] = useState('Code de la route');
  const [description, setDescription] = useState('');
  const [progress, setProgress] = useState(null);
  const [mine, setMine] = useState([]);
  const [cgu, setCgu] = useState(false);
  const [cguWarn, setCguWarn] = useState(false);
  const [justSent, setJustSent] = useState(false);

  const token = localStorage.getItem('cv_token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadMine = () => {
    axios.get(`${API}/shorts/mine`, { headers: authHeaders }).then((r) => setMine(r.data)).catch(() => {});
  };
  useEffect(() => { if (user) loadMine(); /* eslint-disable-next-line */ }, [user]);

  if (loading) return <PageShell><div className="max-w-3xl mx-auto px-4 py-20 text-white/50">Chargement…</div></PageShell>;

  if (!user) {
    return (
      <PageShell>
        <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="proposer-short-locked">
          <span className="inline-flex w-14 h-14 rounded-2xl bg-white/[0.05] border border-white/10 items-center justify-center mb-5"><Clapperboard size={22} className="text-red-400" /></span>
          <h1 className="font-serif-editorial text-white text-[26px] sm:text-[32px]">Propose ton short à la communauté</h1>
          <p className="text-white/55 text-[13.5px] mt-3 leading-relaxed">Connecte-toi avec ton compte élève ou enseignant pour envoyer une vidéo courte (moins de 30 s idéalement). Après validation par l'équipe, elle rejoint l'onglet Shorts.</p>
          <div className="mt-6 grid grid-cols-3 gap-2 text-left">
            {[
              { n: '1', t: 'Tu proposes', d: 'Lien YouTube ou fichier MP4' },
              { n: '2', t: 'On valide', d: 'Rien ne se publie sans accord' },
              { n: '3', t: 'C\'est en ligne', d: 'Avec ton prénom dans le fil' },
            ].map(({ n, t, d }) => (
              <div key={n} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="w-5 h-5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">{n}</span>
                  <span className="text-white text-[11.5px] font-bold">{t}</span>
                </div>
                <p className="text-white/50 text-[10.5px] leading-snug">{d}</p>
              </div>
            ))}
          </div>
          <Link to="/videos/conditions" data-testid="proposer-locked-conditions-link" className="mt-4 inline-block text-[12.5px] text-red-400 hover:text-red-300 font-semibold">
            Lire l'intégralité des modalités et conditions de contribution →
          </Link>
          <div className="flex items-center justify-center gap-2 mt-5">
            <button type="button" onClick={openLogin} data-testid="proposer-login-btn"
              className="inline-flex h-11 px-6 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-bold items-center transition">
              Se connecter
            </button>
            <button type="button" onClick={openRegister} data-testid="proposer-register-btn"
              className="inline-flex h-11 px-6 rounded-full border border-white/15 text-white/90 hover:border-white/40 text-[13px] font-semibold items-center transition">
              Créer un compte
            </button>
          </div>
        </div>
      </PageShell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error('Donne un titre à ta vidéo'); return; }
    if (!cgu) {
      setCguWarn(true);
      toast.error('Coche la case des conditions de contribution juste au-dessus du bouton');
      document.querySelector('[data-testid="short-cgu-label"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (mode === 'link') {
      if (!url.trim().startsWith('https://')) { toast.error('Colle un lien HTTPS (YouTube, Vimeo…)'); return; }
      try {
        await axios.post(`${API}/shorts/submit-link`, { url, title, theme, description, cgu_accepted: true }, { headers: authHeaders });
        toast.success('Lien envoyé ! Il sera publié après validation.');
        setUrl(''); setTitle(''); setDescription('');
        setJustSent(true);
        loadMine();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Lien refusé, vérifie l'URL");
      }
      return;
    }
    if (!file) { toast.error('Choisis une vidéo (MP4 vertical de préférence)'); return; }
    if (file.size > 80 * 1024 * 1024) { toast.error('80 Mo maximum — raccourcis ou compresse ta vidéo'); return; }
    const uploadId = makeUploadId();
    const total = Math.ceil(file.size / CHUNK);
    setProgress(0);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', i);
        fd.append('chunk', file.slice(i * CHUNK, (i + 1) * CHUNK));
        await axios.post(`${API}/shorts/upload-chunk`, fd, { headers: authHeaders });
        setProgress(Math.round(((i + 1) / total) * 90));
      }
      const fd = new FormData();
      fd.append('upload_id', uploadId);
      fd.append('title', title);
      fd.append('theme', theme);
      fd.append('description', description);
      fd.append('cgu_accepted', 'true');
      await axios.post(`${API}/shorts/submit`, fd, { headers: authHeaders });
      setProgress(100);
      toast.success('Short envoyé ! Il sera publié après validation.');
      setFile(null); setTitle(''); setDescription('');
      setJustSent(true);
      loadMine();
      setTimeout(() => setProgress(null), 1200);
    } catch (err) {
      setProgress(null);
      toast.error(err.response?.data?.detail || "Échec de l'envoi, réessaie");
    }
  };

  return (
    <PageShell>
      <div className="max-w-3xl mx-auto px-4 mt-8 sm:mt-12 pb-16" data-testid="proposer-short-page">
        <Link to="/shorts" className="inline-flex items-center gap-1.5 text-white/50 text-[12.5px] hover:text-white mb-4"><ArrowLeft size={13} /> Retour aux Shorts</Link>
        <SectionTitle
          kicker="Communauté · Shorts"
          title="Propose ta leçon en 30 secondes."
          sub="Élève ou enseignant : filme une situation de conduite (format vertical, 30 s max idéalement), on la valide, elle rejoint le fil des Shorts avec ton prénom."
        />

        {/* Comment ça marche */}
        <div className="mt-6 grid sm:grid-cols-3 gap-3" data-testid="proposer-how-it-works">
          {[
            { n: '1', icon: Link2, t: 'Tu proposes', d: 'Un lien YouTube/Vimeo… ou ton fichier vidéo (MP4, 80 Mo max), avec un titre et un thème.' },
            { n: '2', icon: ShieldCheck, t: 'On valide', d: 'L\'équipe vérifie chaque vidéo (règles, droits, image). Rien n\'est publié sans validation.' },
            { n: '3', icon: Clapperboard, t: 'C\'est en ligne', d: 'Ta vidéo rejoint le fil Shorts et la galerie Vidéos, avec ton prénom. Tu suis son statut ici.' },
          ].map(({ n, icon: Icon, t, d }) => (
            <div key={n} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="w-6 h-6 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">{n}</span>
                <Icon size={14} className="text-red-400" />
                <span className="text-white text-[13px] font-bold">{t}</span>
              </div>
              <p className="text-white/55 text-[12px] leading-snug">{d}</p>
            </div>
          ))}
        </div>
        <Link to="/videos/conditions" data-testid="proposer-conditions-link"
          className="mt-3 inline-flex items-center gap-2 text-[12.5px] text-red-400 hover:text-red-300 font-semibold">
          <Scale size={13} /> Lire l'intégralité des modalités et conditions de contribution →
        </Link>

        <form onSubmit={submit} className="mt-6 rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7 space-y-4">
          {/* Mode : lien ou fichier */}
          <div className="flex gap-2" data-testid="short-mode-tabs">
            <button type="button" onClick={() => setMode('link')} data-testid="short-mode-link"
              className={`flex-1 h-11 rounded-xl text-[13px] font-bold border transition ${mode === 'link' ? 'bg-red-600 border-red-500 text-white' : 'bg-white/[0.03] border-white/10 text-white/60'}`}>
              🔗 Lien vidéo (YouTube…)
            </button>
            <button type="button" onClick={() => setMode('file')} data-testid="short-mode-file"
              className={`flex-1 h-11 rounded-xl text-[13px] font-bold border transition ${mode === 'file' ? 'bg-red-600 border-red-500 text-white' : 'bg-white/[0.03] border-white/10 text-white/60'}`}>
              📁 Mon fichier vidéo
            </button>
          </div>
          {mode === 'link' ? (
            <label className="block">
              <span className="text-white/60 text-[11px] uppercase tracking-widest font-mono-spec font-bold">Lien de la vidéo</span>
              <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="short-url-input"
                placeholder="https://www.youtube.com/watch?v=…"
                className="mt-2 w-full h-11 px-4 rounded-xl bg-white/[0.04] border border-white/10 text-white text-[14px] focus:border-red-500 outline-none" />
              <span className="block mt-1.5 text-white/40 text-[11px]">{PLATFORMS_HINT}</span>
              <span className="block mt-1 text-white/35 text-[10.5px] leading-snug">Proposer un lien ne vaut pas republication : la vidéo reste lue via le lecteur de sa plateforme d'origine, sans copie.</span>
            </label>
          ) : (
          <label className="block">
            <span className="text-white/60 text-[11px] uppercase tracking-widest font-mono-spec font-bold">Vidéo (MP4 · 80 Mo max)</span>
            <label data-testid="short-file-drop" className={`mt-2 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 cursor-pointer transition ${file ? 'border-emerald-600/50 bg-emerald-950/10' : 'border-white/15 bg-white/[0.02] hover:bg-white/[0.05]'}`}>
              {file ? <><Film size={22} className="text-emerald-400" /><span className="text-white text-[13px] font-semibold">{file.name}</span><span className="text-white/45 text-[11px]">{(file.size / 1024 / 1024).toFixed(1)} Mo</span></>
                : <><UploadCloud size={22} className="text-red-400" /><span className="text-white/70 text-[13px]">Touche pour choisir ta vidéo</span><span className="text-white/40 text-[11px]">Vertical 9:16 recommandé · 30 s max idéalement</span></>}
              <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" data-testid="short-file-input"
                onClick={(e) => { e.target.value = null; }}
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </label>
          )}
          <label className="block">
            <span className="text-white/60 text-[11px] uppercase tracking-widest font-mono-spec font-bold">Titre</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={90} data-testid="short-title-input"
              placeholder="Ex. : Le créneau en 3 repères"
              className="mt-2 w-full h-11 px-4 rounded-xl bg-white/[0.04] border border-white/10 text-white text-[14px] focus:border-red-500 outline-none" />
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-white/60 text-[11px] uppercase tracking-widest font-mono-spec font-bold">Thème</span>
              <select value={theme} onChange={(e) => setTheme(e.target.value)} data-testid="short-theme-select"
                className="mt-2 w-full h-11 px-3 rounded-xl bg-white/[0.04] border border-white/10 text-white text-[14px] outline-none">
                {THEMES.map((t) => <option key={t} value={t} className="bg-[#111]">{t}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-white/60 text-[11px] uppercase tracking-widest font-mono-spec font-bold">Description (optionnel)</span>
              <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={300} data-testid="short-desc-input"
                placeholder="La règle ou l'astuce expliquée"
                className="mt-2 w-full h-11 px-4 rounded-xl bg-white/[0.04] border border-white/10 text-white text-[14px] focus:border-red-500 outline-none" />
            </label>
          </div>
          {progress !== null && (
            <div data-testid="short-upload-progress">
              <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-red-500 transition-all" style={{ width: `${progress}%` }} /></div>
              <div className="text-white/50 text-[11px] font-mono-spec mt-1">{progress < 100 ? `Envoi… ${progress}%` : 'Envoyé ✓'}</div>
            </div>
          )}
          <label className={`flex items-start gap-3 rounded-xl border p-3.5 cursor-pointer transition ${cguWarn && !cgu ? 'border-red-500 bg-red-950/25 animate-pulse' : 'border-white/10 bg-white/[0.02]'}`} data-testid="short-cgu-label">
            <input type="checkbox" checked={cgu} onChange={(e) => { setCgu(e.target.checked); if (e.target.checked) setCguWarn(false); }} data-testid="short-cgu-checkbox"
              className="mt-0.5 w-4 h-4 accent-red-600 shrink-0" />
            <span className="text-white/60 text-[12px] leading-snug">
              J'ai lu et j'accepte les <Link to="/videos/conditions" onClick={(e) => e.stopPropagation()} className="text-red-400 font-semibold hover:text-red-300 underline underline-offset-2">conditions de contribution</Link> : je suis l'auteur de la vidéo ou j'en détiens les droits, toute personne identifiable a donné son accord (plaques floutées), et je comprends que la publication n'est pas garantie et reste soumise à validation. Mineur : accord parental requis.
            </span>
          </label>
          <button type="submit" disabled={progress !== null && progress < 100} data-testid="short-submit-btn"
            className="w-full h-12 rounded-xl bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-[14px] font-bold inline-flex items-center justify-center gap-2 transition">
            <Clapperboard size={16} /> Envoyer pour validation
          </button>
          {justSent && (
            <div className="rounded-xl border border-emerald-600/40 bg-emerald-950/25 p-3.5 flex items-start gap-2.5" data-testid="short-sent-banner">
              <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-emerald-200/90 text-[12.5px] leading-snug">
                C'est reçu ! Ta vidéo est <strong>en attente de validation</strong> par l'équipe — tu seras prévenu par email.
                Suis son statut ci-dessous ou dans <Link to={user?.role === 'enseignant' ? '/enseignant/mes-shorts' : '/eleve/mes-shorts'} className="underline underline-offset-2 font-semibold">Mes shorts</Link>.
              </p>
            </div>
          )}
        </form>

        {mine.length > 0 && (
          <div className="mt-8" data-testid="mes-shorts">
            {mine.some((s) => s.status === 'approved') && (
              <div className="mb-4 rounded-2xl border border-amber-500/40 bg-gradient-to-r from-amber-950/40 to-[#0d0d0e] p-4 flex items-center gap-3.5" data-testid="badge-contributeur">
                <span className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-900/40 shrink-0">
                  <Award size={22} className="text-white" />
                </span>
                <div>
                  <div className="text-amber-300 text-[14px] font-bold">Contributeur CodeVoiture</div>
                  <div className="text-white/55 text-[12px] mt-0.5">
                    {mine.filter((s) => s.status === 'approved').length} vidéo{mine.filter((s) => s.status === 'approved').length > 1 ? 's' : ''} publiée{mine.filter((s) => s.status === 'approved').length > 1 ? 's' : ''} dans le fil Shorts — merci de faire vivre la communauté !
                  </div>
                </div>
              </div>
            )}
            <h3 className="font-serif-editorial text-white text-[20px] mb-3">Mes shorts envoyés</h3>
            <div className="space-y-2">
              {mine.map((s) => {
                const st = STATUS_UI[s.status] || STATUS_UI.pending;
                const Icon = st.icon;
                return (
                  <div key={s.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3.5" data-testid={`mon-short-${s.id}`}>
                    <Film size={16} className="text-white/40 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-[13.5px] font-semibold truncate">{s.title}</div>
                      <div className="text-white/45 text-[11px]">{s.theme}{s.reject_reason ? ` · Motif : ${s.reject_reason}` : ''}</div>
                    </div>
                    <span className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10.5px] font-bold ${st.cl}`}>
                      <Icon size={11} /> {st.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}
